<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="title1" />

        <div class="card business-report-card">
          <div class="card-body">
            <div class="report-header-bar">
              <div>
                <h5>Business Report</h5>
                <p>Sales, cost, profit and expenses for one day or a whole month, with payments made to suppliers.</p>
              </div>
            </div>
            <div class="report-header">
              <div class="report-tabs">
                <button type="button" :class="{ active: activeTab === 'daily' }" @click="switchTab('daily')">Daily Report</button>
                <button type="button" :class="{ active: activeTab === 'monthly' }" @click="switchTab('monthly')">Monthly Report</button>
              </div>
              <div class="export-actions">
                <button class="btn btn-outline-success" type="button" :disabled="exportLoading" @click="exportReport('excel')">
                  <vue-feather type="download" size="15"></vue-feather> Excel
                </button>
                <button class="btn btn-outline-danger" type="button" :disabled="exportLoading" @click="exportReport('pdf')">
                  <vue-feather type="file-text" size="15"></vue-feather> PDF
                </button>
              </div>
            </div>

            <div class="report-toolbar">
              <div v-if="activeTab === 'daily'" class="filter-row">
                <div class="form-group">
                  <label>Date</label>
                  <input v-model="dailyDate" type="date" class="form-control" />
                </div>
                <button class="btn btn-submit filter-btn" type="button" @click="loadDailyReport">View Report</button>
              </div>
              <div v-else class="filter-row">
                <div class="form-group">
                  <label>Month</label>
                  <input v-model="monthlyValue" type="month" class="form-control" />
                </div>
                <button class="btn btn-submit filter-btn" type="button" @click="loadMonthlyReport">View Report</button>
              </div>
            </div>

            <div class="summary-grid" :class="{ monthly: activeTab === 'monthly' }">
              <div class="summary-card">
                <span>{{ activeTab === 'monthly' ? 'Total Cost (Buy Price)' : 'Cost (Buy Price)' }}</span>
                <strong>₹{{ formatCurrency(summary.buy_price) }}</strong>
              </div>
              <div class="summary-card">
                <span>{{ activeTab === 'monthly' ? 'Total Sales' : 'Sales' }}</span>
                <strong>₹{{ formatCurrency(summary.sale_price) }}</strong>
              </div>
              <div :class="['summary-card', Number(summary.profit) < 0 ? 'tone-negative' : 'tone-positive']">
                <span>Profit</span>
                <strong>₹{{ formatCurrency(summary.profit) }}</strong>
              </div>
              <div class="summary-card">
                <span>Profit Margin</span>
                <strong>{{ formatPercent(summary.profit_percentage) }}</strong>
              </div>
              <div v-if="activeTab === 'monthly'" class="summary-card">
                <span>Total Expenses</span>
                <strong>₹{{ formatCurrency(summary.expense) }}</strong>
              </div>
            </div>

            <template v-if="activeTab === 'daily'">
              <section class="report-section">
                <h5>Business/Sales</h5>
                <div class="table-responsive">
                  <a-table :columns="dailySalesColumns" :data-source="dailySalesRows" :loading="loading" :pagination="false" row-key="rowkey" :row-class-name="rowClassName">
                    <template #bodyCell="{ column, record }">
                      <span :class="{ 'total-cell': record._isTotal }">
                        <template v-if="moneyColumns.includes(column.key)">{{ formatCurrency(record[column.key]) }}</template>
                        <template v-else-if="column.key === 'profit_percentage'">{{ formatPercent(record.profit_percentage) }}</template>
                        <template v-else>{{ displayCell(record[column.key]) }}</template>
                      </span>
                    </template>
                  </a-table>
                </div>
                <p v-if="!loading && !dailyReport.sales.length" class="empty-state">No business transactions found for this date.</p>
              </section>

              <section class="report-section">
                <h5>Expenses</h5>
                <div class="table-responsive">
                  <a-table :columns="expenseColumns" :data-source="dailyExpenseRows" :loading="loading" :pagination="false" row-key="rowkey" :row-class-name="rowClassName">
                    <template #bodyCell="{ column, record }">
                      <span :class="{ 'total-cell': record._isTotal }">
                        <template v-if="column.key === 'amount'">{{ formatCurrency(record.amount) }}</template>
                        <template v-else>{{ displayCell(record[column.key]) }}</template>
                      </span>
                    </template>
                  </a-table>
                </div>
                <p v-if="!loading && !dailyReport.expenses.length" class="empty-state">No expenses recorded.</p>
              </section>
            </template>

            <template v-else>
              <section class="report-section">
                <h5>Monthly Business/Sales</h5>
                <div class="table-responsive">
                  <a-table :columns="monthlyColumns" :data-source="monthlyRows" :loading="loading" :pagination="false" row-key="rowkey" :row-class-name="rowClassName">
                    <template #bodyCell="{ column, record }">
                      <span :class="{ 'total-cell': record._isTotal }">
                        <template v-if="column.key === 'date'">{{ record._isTotal ? 'Total' : formatShortDate(record.date) }}</template>
                        <template v-else-if="moneyColumns.includes(column.key)">{{ formatCurrency(record[column.key]) }}</template>
                        <template v-else-if="column.key === 'profit_percentage'">{{ formatPercent(record.profit_percentage) }}</template>
                        <template v-else>{{ displayCell(record[column.key]) }}</template>
                      </span>
                    </template>
                  </a-table>
                </div>
                <p v-if="!loading && !monthlyReport.days.length" class="empty-state">No data found for this month.</p>
              </section>
            </template>

            <section class="report-section">
              <h5>Vendor Payments</h5>
              <div class="table-responsive">
                <a-table :columns="vendorColumns" :data-source="vendorRows" :loading="loading" :pagination="false" row-key="rowkey" :row-class-name="rowClassName">
                  <template #bodyCell="{ column, record }">
                    <span :class="{ 'total-cell': record._isTotal }">
                      <template v-if="column.key === 'date'">{{ record._isTotal ? 'Total' : formatShortDate(record.date) }}</template>
                      <template v-else-if="column.key === 'no' && record._isTotal && activeTab === 'daily'">Total</template>
                      <template v-else-if="column.key === 'amount'">{{ formatCurrency(record.amount) }}</template>
                      <template v-else>{{ displayCell(record[column.key]) }}</template>
                    </span>
                  </template>
                </a-table>
              </div>
              <p v-if="!loading && !vendorPayments.length" class="empty-state">No vendor payments recorded.</p>
            </section>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import { downloadExport, errorMessage } from "@/services/reports";

const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

const currentMonth = () => today().slice(0, 7);

const emptyReport = () => ({
  summary: { buy_price: 0, sale_price: 0, profit: 0, profit_percentage: 0, expense: 0 },
  sales: [],
  expenses: [],
  vendor_payments: [],
  days: [],
  totals: { buy_price: 0, sale_price: 0, profit: 0, profit_percentage: 0, expense: 0, vendor_payments: 0 },
});

export default {
  name: "businessreport",
  data() {
    return {
      title: "Business Reports",
      title1: "Daily and monthly sales, expense, and vendor payment reports",
      activeTab: "daily",
      dailyDate: today(),
      monthlyValue: currentMonth(),
      dailyReport: emptyReport(),
      monthlyReport: emptyReport(),
      loading: false,
      exportLoading: false,
      moneyColumns: ["buy_price", "sale_price", "profit", "expense", "amount"],
      dailySalesColumns: [
        { title: "#", dataIndex: "no", key: "no", width: 70 },
        { title: "Item Name", dataIndex: "itemname", key: "itemname", width: 260 },
        { title: "Buy Price", dataIndex: "buy_price", key: "buy_price", align: "right" },
        { title: "Sale Price", dataIndex: "sale_price", key: "sale_price", align: "right" },
        { title: "Profit", dataIndex: "profit", key: "profit", align: "right" },
        { title: "Margin %", dataIndex: "profit_percentage", key: "profit_percentage", align: "right" },
      ],
      expenseColumns: [
        { title: "#", dataIndex: "no", key: "no", width: 70 },
        { title: "Item", dataIndex: "item", key: "item" },
        { title: "Amount", dataIndex: "amount", key: "amount", align: "right" },
      ],
      monthlyColumns: [
        { title: "Date", dataIndex: "date", key: "date" },
        { title: "Buy Price", dataIndex: "buy_price", key: "buy_price", align: "right" },
        { title: "Sale Price", dataIndex: "sale_price", key: "sale_price", align: "right" },
        { title: "Profit", dataIndex: "profit", key: "profit", align: "right" },
        { title: "Margin %", dataIndex: "profit_percentage", key: "profit_percentage", align: "right" },
        { title: "Expense", dataIndex: "expense", key: "expense", align: "right" },
      ],
    };
  },
  computed: {
    summary() {
      return this.activeTab === "daily" ? this.dailyReport.summary : this.monthlyReport.summary;
    },
    dailySalesRows() {
      const rows = (this.dailyReport.sales || []).map((row) => ({ ...row, rowkey: `sale-${row.no}` }));
      return rows.concat({
        rowkey: "daily-sales-total",
        no: "Total",
        itemname: "",
        buy_price: this.dailyReport.totals?.buy_price || 0,
        sale_price: this.dailyReport.totals?.sale_price || 0,
        profit: this.dailyReport.totals?.profit || 0,
        profit_percentage: this.dailyReport.totals?.profit_percentage || 0,
        _isTotal: true,
      });
    },
    dailyExpenseRows() {
      const rows = (this.dailyReport.expenses || []).map((row) => ({ ...row, rowkey: `expense-${row.no}` }));
      return rows.concat({
        rowkey: "daily-expense-total",
        no: "Total",
        item: "",
        amount: this.dailyReport.totals?.expense || 0,
        _isTotal: true,
      });
    },
    monthlyRows() {
      const rows = (this.monthlyReport.days || []).map((row) => ({ ...row, rowkey: `month-${row.date}` }));
      return rows.concat({
        rowkey: "monthly-total",
        date: "Total",
        buy_price: this.monthlyReport.totals?.buy_price || 0,
        sale_price: this.monthlyReport.totals?.sale_price || 0,
        profit: this.monthlyReport.totals?.profit || 0,
        profit_percentage: this.monthlyReport.totals?.profit_percentage || 0,
        expense: this.monthlyReport.totals?.expense || 0,
        _isTotal: true,
      });
    },
    vendorPayments() {
      return this.activeTab === "daily" ? this.dailyReport.vendor_payments : this.monthlyReport.vendor_payments;
    },
    vendorPaymentTotal() {
      const report = this.activeTab === "daily" ? this.dailyReport : this.monthlyReport;
      return report.totals?.vendor_payments || 0;
    },
    vendorRows() {
      const rows = (this.vendorPayments || []).map((row) => ({ ...row, rowkey: `vendor-${row.no}-${row.date || ""}` }));
      return rows.concat({
        rowkey: "vendor-total",
        date: "Total",
        no: this.activeTab === "daily" ? "Total" : "",
        distributor: "",
        amount: this.vendorPaymentTotal,
        _isTotal: true,
      });
    },
    vendorColumns() {
      const columns = [];
      if (this.activeTab === "monthly") columns.push({ title: "Date", dataIndex: "date", key: "date" });
      columns.push(
        { title: "#", dataIndex: "no", key: "no", width: 70 },
        { title: "Supplier", dataIndex: "distributor", key: "distributor" },
        { title: "Amount", dataIndex: "amount", key: "amount", align: "right" },
      );
      return columns;
    },
  },
  mounted() {
    this.loadDailyReport();
  },
  methods: {
    switchTab(tab) {
      if (this.activeTab === tab) return;
      this.activeTab = tab;
      if (tab === "daily" && !this.dailyReport.date) this.loadDailyReport();
      if (tab === "monthly" && !this.monthlyReport.start_date) this.loadMonthlyReport();
    },
    async loadDailyReport() {
      this.loading = true;
      try {
        const response = await axios.get("/v1/businessreport/daily", {
          params: { report_date: this.dailyDate },
        });
        this.dailyReport = { ...emptyReport(), ...response.data };
      } catch (error) {
        Swal.fire({ icon: "error", title: "Failed to load daily report", text: error.response?.data?.detail || error.message });
      } finally {
        this.loading = false;
      }
    },
    async loadMonthlyReport() {
      const [year, month] = this.monthlyValue.split("-").map(Number);
      this.loading = true;
      try {
        const response = await axios.get("/v1/businessreport/monthly", {
          params: { year, month },
        });
        this.monthlyReport = { ...emptyReport(), ...response.data };
      } catch (error) {
        Swal.fire({ icon: "error", title: "Failed to load monthly report", text: error.response?.data?.detail || error.message });
      } finally {
        this.loading = false;
      }
    },
    async exportReport(format) {
      this.exportLoading = true;
      try {
        const params = { report_type: this.activeTab, export_format: format };
        if (this.activeTab === "daily") {
          params.report_date = this.dailyDate;
        } else {
          const [year, month] = this.monthlyValue.split("-").map(Number);
          params.year = year;
          params.month = month;
        }
        await downloadExport("/v1/businessreport/export", params, `business_report_${this.activeTab}.${format === "pdf" ? "pdf" : "xlsx"}`);
      } catch (error) {
        Swal.fire({ icon: "error", title: "Export failed", text: await errorMessage(error) });
      } finally {
        this.exportLoading = false;
      }
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    displayCell(value) {
      if (value === 0) return "0";
      return value || "";
    },
    rowClassName(record) {
      return record?._isTotal ? "report-total-row" : "";
    },
    formatPercent(value) {
      return `${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
    },
    formatShortDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
    },
  },
};
</script>

<style scoped src="./report-theme.css"></style>
<style scoped>
.report-header,
.filter-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}
.report-header {
  margin-bottom: 16px;
}
.report-tabs {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f8fafc;
}
.report-tabs button {
  min-height: 34px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #64748b;
  padding: 0 14px;
  font-weight: 700;
}
.report-tabs button.active {
  background: #ffffff;
  color: #1d4ed8;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);
}
.filter-row {
  justify-content: flex-start;
  align-items: flex-end;
}
.form-group {
  margin-bottom: 0;
  min-width: 210px;
}
.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 18px;
}
.summary-grid.monthly {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}
.report-section {
  margin-top: 20px;
}
.report-section h5 {
  margin: 0 0 10px;
  color: #111827;
  font-size: 16px;
}
.report-section :deep(.ant-table) {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}
.report-section :deep(.ant-table-tbody > tr > td) {
  border-bottom: 1px solid #eef2f7;
  color: #111827;
  vertical-align: middle;
}
.report-section :deep(.ant-table-tbody > tr:nth-child(even):not(.report-total-row) > td) {
  background: #fbfdff;
}
.report-section :deep(.ant-table-tbody > tr:hover:not(.report-total-row) > td) {
  background: #f8fafc;
}
.report-section :deep(.report-total-row > td) {
  background: #dbeafe !important;
  border-top: 2px solid #1d4ed8;
  border-bottom: 1px solid #93c5fd;
}
.filter-btn {
  height: 40px;
  min-width: 112px;
  border-radius: 8px;
}
.summary-card.tone-positive {
  border-left-color: #10b981;
}
.summary-card.tone-positive strong {
  color: #047857;
}
.summary-card.tone-negative {
  border-left-color: #ef4444;
}
.summary-card.tone-negative strong {
  color: #b91c1c;
}
.total-cell {
  color: #111827;
  font-weight: 800;
}
.empty-state {
  margin: 10px 0 0;
  color: #64748b;
  font-size: 13px;
}
@media (max-width: 992px) {
  .summary-grid,
  .summary-grid.monthly {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 576px) {
  .report-header,
  .filter-row {
    align-items: stretch;
    flex-direction: column;
  }
  .summary-grid,
  .summary-grid.monthly {
    grid-template-columns: 1fr;
  }
  .form-group,
  .filter-btn {
    width: 100%;
  }
}
</style>
